import { useFormikContext } from 'formik';
import { type MutableRefObject, useContext, useEffect, useMemo, useRef } from 'react';

import { SlabContext } from '@core/context/SlabContext';
import type { ProductsByPlantFormikType, QuoteFormikType } from '@core/formik/quote';
import Enums from '@core/types/api/Enums';
import type { FixedPricingByCompany } from '@core/types/api/FixedPricingByCompany/FixedPricingByCompany';
import type { Product } from '@core/types/api/Product/Product';
import { ExceedingDiscounts } from '@core/utils/discounts';
import { hasStringValue, hasValue } from '@core/utils/equality';
import { SetFormikValue } from '@core/utils/formikHelpers';
import {
  applyBuyerChangeToProductsByPlants,
  applyProjectPricesAsOriginal,
  applyTierPricingToProductsByPlants,
  dropDiscountsFromProductsByPlants,
  withPendingDiscounts,
} from '@core/utils/quoteHelpers';
import { isValidUUID } from '@core/utils/uuid';
import { ServerErrorMessage, useSlabQueryFetcher } from '@web/hooks/useSlabQuery';
import { useTenantMargins } from '@web/hooks/useTenantMargins';
import { useToast } from '@web/providers/ToastProvider/useToast';

import { useQuoteDrawerContext } from '../QuoteDrawer';

export type ProductRowsOverride = Pick<
  QuoteFormikType,
  'mainProductsByPlants' | 'additionalProductsByPlants'
>;

export type BuyerPricingReconciler = {
  reconcileBuyerChange: (newCompanyID: string | null) => Promise<void>;
  reconcileProjectSelection: (
    newCompanyID: string | null,
    rows: ProductRowsOverride,
  ) => Promise<void>;
};

export const useBuyerPricingReconciler = (
  curProductsRef: MutableRefObject<Record<string, Product>>,
): BuyerPricingReconciler => {
  const ctx = useContext(SlabContext);
  const { marginConfiguration, defaultMargins } = useTenantMargins();
  const formikBag = useFormikContext<QuoteFormikType>();
  const latestValuesRef = useRef(formikBag.values);
  latestValuesRef.current = formikBag.values;

  const hasTierPricing =
    ctx.userInfo.hasFlags([Enums.FeatureFlagName.FeatureFlagTierPricing]) &&
    ctx.userInfo.tenant.tierPricing;
  const hasDiscounts = ctx.userInfo.hasFlags([Enums.FeatureFlagName.FeatureFlagDiscounts]);

  const fetchTierPricing = useSlabQueryFetcher<'GET tier pricing by company ID'>();
  const fetchProjectProducts = useSlabQueryFetcher<'GET project products by project ID'>();
  const toast = useToast();
  const { setIsReconcilingBuyerPricing } = useQuoteDrawerContext();
  const latestCallRef = useRef(0);
  const rowClientIdsOf = (rows: ProductRowsOverride): ReadonlySet<string> =>
    new Set(
      [...rows.mainProductsByPlants, ...rows.additionalProductsByPlants].map(
        (plantEntry) => plantEntry.clientId,
      ),
    );
  const pricedRowsRef = useRef({
    companyID: formikBag.values.company?.id ?? null,
    clientIds: rowClientIdsOf(formikBag.values),
  });
  if (!formikBag.dirty) {
    pricedRowsRef.current = {
      companyID: formikBag.initialValues.company?.id ?? null,
      clientIds: rowClientIdsOf(formikBag.values),
    };
  }
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return (): void => {
      isMountedRef.current = false;
      setIsReconcilingBuyerPricing?.(false);
    };
  }, [setIsReconcilingBuyerPricing]);

  const reconcileRef = useRef<
    (newCompanyID: string | null, productsOverride?: ProductRowsOverride) => Promise<void>
  >(async (): Promise<void> => {});
  reconcileRef.current = async (
    newCompanyID: string | null,
    productsOverride?: ProductRowsOverride,
  ): Promise<void> => {
    latestCallRef.current += 1;
    const callToken = latestCallRef.current;
    const rowsFor = (section: keyof ProductRowsOverride): QuoteFormikType[typeof section] =>
      productsOverride?.[section] ?? latestValuesRef.current[section];
    const displayOnlyContext = { curProductsRef, marginConfiguration, defaultMargins };
    const isBuyerCleared = !hasStringValue(newCompanyID);
    const shouldRecheckDiscounts = hasDiscounts || isBuyerCleared;
    const dropDiscountsWithoutBuyer = (
      rows: ProductsByPlantFormikType[],
    ): ProductsByPlantFormikType[] =>
      isBuyerCleared ? dropDiscountsFromProductsByPlants(rows, displayOnlyContext) : rows;
    const recheckDiscounts = (): void => {
      (['mainProductsByPlants', 'additionalProductsByPlants'] as const).forEach((section) => {
        SetFormikValue(
          formikBag,
          section,
          dropDiscountsWithoutBuyer(withPendingDiscounts(rowsFor(section))),
        );
      });
    };
    if (!hasTierPricing) {
      if (!shouldRecheckDiscounts) {
        return;
      }
      recheckDiscounts();
      return;
    }
    setIsReconcilingBuyerPricing?.(true);
    const projectId = latestValuesRef.current.project.id;
    const isProjectSelection = hasValue(productsOverride);
    const pricedRows = pricedRowsRef.current;
    const shownCompanyID = latestValuesRef.current.company?.id ?? null;
    const pricedForCompanyIDOf = (plantEntry: ProductsByPlantFormikType): string | null =>
      pricedRows.clientIds.has(plantEntry.clientId) ? pricedRows.companyID : shownCompanyID;
    const previousCompanyIDs = isProjectSelection
      ? []
      : Array.from(
          new Set(
            [...rowsFor('mainProductsByPlants'), ...rowsFor('additionalProductsByPlants')].map(
              pricedForCompanyIDOf,
            ),
          ),
        );
    const shouldDiscardReply = (): boolean =>
      !isMountedRef.current ||
      latestCallRef.current !== callToken ||
      (isProjectSelection && latestValuesRef.current.project.id !== projectId);
    const fetchTierPricingOf = (
      companyID: string | null,
    ): Promise<FixedPricingByCompany | undefined> =>
      hasStringValue(companyID)
        ? fetchTierPricing('GET tier pricing by company ID', {
            queryParams: { companyIds: [companyID] },
          })
        : Promise.resolve(undefined);
    try {
      const [previousFixedPricings, fixed, projectProducts] = await Promise.all([
        Promise.all(previousCompanyIDs.map(fetchTierPricingOf)),
        fetchTierPricingOf(newCompanyID),
        isValidUUID(latestValuesRef.current.id) && hasStringValue(projectId)
          ? fetchProjectProducts('GET project products by project ID', {
              pathParams: { id: projectId },
            })
          : Promise.resolve(undefined),
      ]);
      if (shouldDiscardReply()) {
        return;
      }
      const sourceBySection = {
        mainProductsByPlants: applyProjectPricesAsOriginal(
          rowsFor('mainProductsByPlants'),
          projectProducts ?? [],
        ),
        additionalProductsByPlants: rowsFor('additionalProductsByPlants'),
      };
      const previousFixedByCompanyID = new Map<string | null, FixedPricingByCompany | undefined>(
        previousCompanyIDs.map((companyID, index) => [companyID, previousFixedPricings[index]]),
      );
      const reprice = (rows: ProductsByPlantFormikType[]): ProductsByPlantFormikType[] =>
        dropDiscountsWithoutBuyer(
          isProjectSelection
            ? applyTierPricingToProductsByPlants(
                rows,
                fixed,
                displayOnlyContext,
                ExceedingDiscounts.Unapply,
                true,
              )
            : rows.flatMap((plantEntry) =>
                applyBuyerChangeToProductsByPlants(
                  [plantEntry],
                  previousFixedByCompanyID.get(pricedForCompanyIDOf(plantEntry)),
                  fixed,
                  displayOnlyContext,
                ),
              ),
        );
      const repricedBySection = {
        mainProductsByPlants: reprice(sourceBySection.mainProductsByPlants),
        additionalProductsByPlants: reprice(sourceBySection.additionalProductsByPlants),
      };
      (['mainProductsByPlants', 'additionalProductsByPlants'] as const).forEach((section) => {
        SetFormikValue(formikBag, section, repricedBySection[section]);
      });
      pricedRowsRef.current = {
        companyID: newCompanyID,
        clientIds: rowClientIdsOf(repricedBySection),
      };
    } catch (error) {
      if (!shouldDiscardReply()) {
        if (shouldRecheckDiscounts) {
          recheckDiscounts();
        }
        toast.error({
          title: 'Could not load buyer pricing',
          message: ServerErrorMessage(
            error,
            isBuyerCleared
              ? 'Discounts were removed and their amounts added back to the prices.'
              : 'Current prices were kept and discounts were rechecked.',
          ),
        });
      }
    } finally {
      if (isMountedRef.current && latestCallRef.current === callToken) {
        setIsReconcilingBuyerPricing?.(false);
      }
    }
  };

  return useMemo(
    () => ({
      reconcileBuyerChange: (newCompanyID: string | null): Promise<void> =>
        reconcileRef.current(newCompanyID),
      reconcileProjectSelection: (
        newCompanyID: string | null,
        rows: ProductRowsOverride,
      ): Promise<void> => reconcileRef.current(newCompanyID, rows),
    }),
    [],
  );
};
